// @flow
import moment from 'moment';
import lodashGet from 'lodash-es/get';
import { t } from '@lingui/macro';

import { fullApiUrl } from '@rover/utilities/apiUrl';
import { getAxiosInstance } from '@rover/utilities/axiosUtils';
import { snakeify, camelize } from '@rover/utilities/object-utilities';
import { DEFAULT_LANG } from '@rover/shared/js/constants/i18n/language';
import I18N_FORMAT_MAP from '@rover/design-system/style-dictionary/build/es6/i18nFormatMap.constants';
import petFactory from '@rover/react-lib/factories/petFactory';
import type {
  ExpressParams,
  Pet,
  PetDetailsOptions,
  FetchPetImageResponse,
  Image,
} from '@rover/react-lib/types';

import { get as personSummaryGet } from './personSummary.api';
import DefaultAPIErrorHandler from './DefaultAPIErrorHandler';

export const PET_LIST_URL = fullApiUrl('pets/');
export const PERSON_SUMMARY_URL = fullApiUrl('me/');

export const scrubAPetData = (
  rawPet: $Shape<Pet> & { birthday: $PropertyType<Pet, 'birthday'> }
): Pet => {
  const ageYears = moment().diff(rawPet.birthday, 'years');
  const ageMonths = moment().diff(rawPet.birthday, 'months') % 12;

  return petFactory({
    ...rawPet,
    ageYears,
    ageMonths,
  });
};

export const scrubRawData = (
  rawPetsData: Array<$Shape<Pet> & { birthday: $PropertyType<Pet, 'birthday'> }>
): Array<Pet> => rawPetsData.map(scrubAPetData);

export const get = async (expressParams: ?ExpressParams): Promise<Array<Pet>> => {
  try {
    const data = await personSummaryGet(undefined, expressParams);
    return scrubRawData(data.pets);
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error while fetching pets.`);
  }
};

export const getDetail = async (petUrl: string, expressParams: ?ExpressParams): Promise<Pet> => {
  const ai = getAxiosInstance(expressParams);

  try {
    const { data } = await ai.get(petUrl);
    return scrubAPetData(camelize(data, true));
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error fetching pet details.`);
  }
};

export const getPetDetailsOptions = async (
  expressParams: ?ExpressParams
): Promise<PetDetailsOptions> => {
  const ai = getAxiosInstance(expressParams);
  const petDetailsOptionsUrl = fullApiUrl('pets/');

  try {
    const response = await ai.request({ url: petDetailsOptionsUrl, method: 'options' });
    const petDetailsOptions = lodashGet(response, 'data.actions.POST.pet_details.child.children');
    return camelize(petDetailsOptions, true);
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error fetching pet details options.`);
  }
};

export const deletePet = async (
  petUrl: string,
  expressParams: ?ExpressParams
): Promise<boolean> => {
  const ai = getAxiosInstance(expressParams);

  try {
    await ai.delete(petUrl);
    return true;
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error deleting pet.`);
  }
};

export const getImages = async (
  url: string,
  pageSize?: number,
  expressParams: ?ExpressParams
): Promise<FetchPetImageResponse> => {
  const ai = getAxiosInstance(expressParams);

  try {
    const { data } = await ai.get(url, { params: { page_size: pageSize } });
    return camelize(data, true);
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error fetching pet images.`);
  }
};

export const deleteImage = async (
  imageUrl: string,
  expressParams: ?ExpressParams
): Promise<boolean> => {
  const ai = getAxiosInstance(expressParams);

  try {
    await ai.delete(imageUrl);
    return true;
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error deleting pet image.`);
  }
};

export const addImage = async (
  petImagesUrl: string,
  imageUrl: string,
  expressParams: ?ExpressParams
): Promise<Image> => {
  const ai = getAxiosInstance(expressParams);

  try {
    const response = await ai.post(petImagesUrl, {
      image_url: imageUrl,
    });
    return camelize(response.data, true);
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error adding a pet image.`);
  }
};

export const setImageToFirst = async (
  orderingUrl: string,
  expressParams: ?ExpressParams
): Promise<void> => {
  const ai = getAxiosInstance(expressParams);

  try {
    ai.put(orderingUrl, {
      previous: null,
    });
  } catch (e) {
    throw DefaultAPIErrorHandler(e, t`There was an error updating the pet image.`);
  }
};

export const postOrPut = async (pet: $Shape<Pet>, expressParams: ?ExpressParams): Promise<Pet> => {
  const ai = getAxiosInstance(expressParams);
  const birthday = moment()
    .subtract(pet.ageYears, 'years')
    .subtract(pet.ageMonths, 'months')
    .format(I18N_FORMAT_MAP[DEFAULT_LANG].DATE_ISO);
  let rawData = {
    ...pet,
    birthday,
  };

  rawData = Object.keys(rawData)
    .filter(k => rawData[k] !== null)
    .reduce((obj, k) => {
      obj[k] = rawData[k]; // eslint-disable-line no-param-reassign
      return obj;
    }, {});

  try {
    const snakeData = snakeify(rawData, true);
    const { data } = await ai({
      method: pet.url ? 'PUT' : 'POST',
      url: pet.url ? pet.url : PET_LIST_URL,
      data: {
        ...snakeData,
      },
    });
    return scrubAPetData(camelize(data, true));
  } catch (e) {
    return DefaultAPIErrorHandler(e, t`There was an error saving your pet information.`);
  }
};



// WEBPACK FOOTER //
// ../react-lib/src/apis/pet.api.js